import { useEffect, useRef, type ReactNode } from 'react';
import { useFocusTrap } from './FocusTrap';

export type ModalIntent = 'default' | 'warning' | 'danger';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** モーダル横幅。デフォルト 'md' (max-w-md)。`xl` は 3 カラム詳細など広い content 用。 */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** overlay click で閉じるか。デフォルト true。フォーム編集中は false 推奨。 */
  closeOnOverlayClick?: boolean;
  /**
   * 意味づけ。`warning` / `danger` のときヘッダにアイコン + 色を付与する。
   * 既存 consumer は省略で `'default'` 扱い (後方互換)。Sprint AO (docs/16 §10-2 P2 H7) 導入。
   */
  intent?: ModalIntent;
  /**
   * focus trap (Tab/Shift+Tab で内部 focus を閉じ込め) を有効にするか。
   * Sprint BA (a11y 横断) で追加。default true。
   * 既存 test は影響を受けないが、無効化したい場合のみ false 指定。
   */
  trapFocus?: boolean;
  children: ReactNode;
}

const SIZE_CLASS: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-4xl',
};

const INTENT_TITLE_CLASS: Record<ModalIntent, string> = {
  default: 'text-slate-900',
  warning: 'text-warning-700',
  danger: 'text-danger-700',
};

const INTENT_ICON: Record<ModalIntent, string | null> = {
  default: null,
  warning: '⚠️',
  danger: '⚠️',
};

/**
 * モーダルダイアログ。
 * 旧 SettingsModal.tsx 等の overlay pattern を踏襲 (docs/97 §3 移植ログ):
 *   - mobile: bottom-sheet 形 (items-end + rounded-t-xl)
 *   - desktop: center 形 (sm:items-center + sm:rounded-xl)
 *   - max-h 90vh + overflow-auto
 *
 * 旧との差分 (a11y 改善、docs/97 §5-1):
 *   - Esc キーで閉じる
 *   - role="dialog" + aria-modal
 *   - body の overflow を hidden に
 */
export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  closeOnOverlayClick = true,
  intent = 'default',
  trapFocus = true,
  children,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  // open=true のときのみ focus trap を ON。Sprint BA、既存 Esc / body lock は不変。
  useFocusTrap(panelRef, { active: open && trapFocus });

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50"
      onClick={closeOnOverlayClick ? onClose : undefined}
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={panelRef}
        className={`bg-white w-full ${SIZE_CLASS[size]} rounded-t-xl sm:rounded-xl p-4 sm:p-6 max-h-[90vh] min-w-0 overflow-auto shadow-lg`}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="flex items-start justify-between mb-4 gap-3">
            <h2
              className={`text-lg sm:text-xl font-bold leading-tight break-words min-w-0 ${INTENT_TITLE_CLASS[intent]}`}
              data-intent={intent}
            >
              {INTENT_ICON[intent] && (
                <span aria-hidden="true" className="mr-2">
                  {INTENT_ICON[intent]}
                </span>
              )}
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-slate-500 hover:text-slate-900 text-2xl leading-none min-h-touch min-w-touch shrink-0"
              aria-label="閉じる"
            >
              ×
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
